// frontend/modules/nub-user/modules/access-level/views/AccessLevelManagement.tsx
import React from 'react';
import { Card, Typography, Input, Space, Button } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { AccessLevelList } from '../components/AccessLevelList';
import { AccessLevelFormDialog } from '../components/AccessLevelFormDialog';
import { useAccessLevel } from '../hooks/use-access-level';
import { NubAccessLevelDTO } from '../types';

const { Title } = Typography;
const { Search } = Input;

export const AccessLevelManagement: React.FC = () => {
    const { dark } = useTheme();
    const {
        // Data
        accessLevels,
        activeAccessLevel,
        permissions,
        selectedPermissions,
        permissionCategories,
        searchTerm,
        actionMode,
        isModalOpen,
        isLoading,
        error,

        // Actions
        handleAccessLevelAction,
        setModalOpen,
        handlePermissionChange,
        setSearchTerm,

        // CRUD operations
        createAccessLevel,
        updateAccessLevel,

        // Utility functions
        getPermissionsByCategory,
    } = useAccessLevel();

    // Filter access levels based on search term
    const filteredAccessLevels = React.useMemo(() => {
        if (!searchTerm) return accessLevels;

        const searchLower = searchTerm.toLowerCase();
        return accessLevels.filter(level =>
            // @ts-ignore
            level.code.toLowerCase().includes(searchLower) ||
            // @ts-ignore
            level.name.toLowerCase().includes(searchLower) ||
            // @ts-ignore
            level.description?.toLowerCase().includes(searchLower) ||
            level.permissions?.some(p =>
                // @ts-ignore
                p.name.toLowerCase().includes(searchLower) ||
                // @ts-ignore
                p.code.toLowerCase().includes(searchLower)
            )
        );
    }, [accessLevels, searchTerm]);

    // Handle form submission
    const handleSubmit = async (formData: NubAccessLevelDTO) => {
        try {
            if (actionMode === 'add') {
                await createAccessLevel({
                    ...formData,
                    permissions: permissions.filter(p =>
                        // @ts-ignore
                        selectedPermissions.includes(p.code)
                    )
                });
            } else if (actionMode === 'edit' && activeAccessLevel) {
                // @ts-ignore
                await updateAccessLevel(activeAccessLevel.code, {
                    ...formData,
                    permissions: permissions.filter(p =>
                        // @ts-ignore
                        selectedPermissions.includes(p.code)
                    )
                });
            }
        } catch (error) {
            console.error('Operation failed:', error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <Title level={2} className={dark ? 'text-white' : ''}>
                    Access Level Management
                </Title>

                <Space>
                    <Search
                        placeholder="Search access levels..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ width: 300 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleAccessLevelAction(null, 'add')}
                    >
                        Add Access Level
                    </Button>
                </Space>
            </div>

            <Card
                className={dark ? 'bg-gray-800' : ''}
                bordered={false}
                loading={isLoading}
            >
                {error ? (
                    <div className="text-red-500">
                        Error: {(error as Error)?.message || 'Unknown error'}
                    </div>
                ) : (
                    <AccessLevelList
                        accessLevels={filteredAccessLevels}
                        onAccessLevelAction={handleAccessLevelAction}
                    />
                )}
            </Card>

            <AccessLevelFormDialog
                open={isModalOpen}
                mode={actionMode}
                initialData={activeAccessLevel}
                // @ts-ignore
                permissionCategories={permissionCategories}
                permissions={permissions}
                selectedPermissions={selectedPermissions}
                getPermissionsByCategory={getPermissionsByCategory}
                onPermissionChange={handlePermissionChange}
                onCancel={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />
        </div>
    );
};